"use client";

import { useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import { useAuthStore } from "@/stores/auth-store";
import { useGameStore } from "@/stores/game-store";
import { useUIStore } from "@/stores/ui-store";
import { HudBar } from "@/components/hud/HudBar";
import { Minimap } from "@/components/game/Minimap";
import { loadMap } from "@/engine/world-map";
import { ChatPanel } from "@/components/hud/ChatPanel";
import { SectionPanel } from "@/components/hud/SectionPanel";
import { NotificationToast } from "@/components/hud/NotificationToast";
import { TasksPanel } from "@/components/hud/TasksPanel";
import GameCanvas from "@/components/game/GameCanvas";
import { GameErrorBoundary } from "@/components/game/GameErrorBoundary";
import { GameSystemsProvider } from "@/components/game/GameSystemsProvider";
import { useGameData } from "@/hooks/use-game-data";
import { useDelegationStream } from "@/hooks/use-delegation-stream";

const GameCanvas3D = dynamic(
  () => import("@/components/game/GameCanvas3D"),
  { ssr: false }
);

function MinimapHost() {
  const playerPosition = useGameStore((s) => s.playerPosition);
  const surggies = useGameStore((s) => s.surggies);
  const departments = useGameStore((s) => s.departments);
  const showMiniMap = useUIStore((s) => s.showMiniMap);
  if (!showMiniMap) return null;
  return (
    <Minimap
      getTileMap={() => loadMap("overworld")?.tileMap ?? null}
      playerPos={playerPosition}
      surggies={surggies}
      buildings={departments.map((d) => ({ id: d.id, x: d.gridX, y: d.gridY }))}
    />
  );
}

function GamePageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const use3D = searchParams.get("engine") === "3d";
  const user = useAuthStore((s) => s.user);
  const activePanel = useUIStore((s) => s.activePanel);
  const setActivePanel = useUIStore((s) => s.setActivePanel);
  const setActiveSurggieId = useUIStore((s) => s.setActiveSurggieId);

  const { loaded: gameDataLoaded, authFailed } = useGameData();

  useEffect(() => {
    if (!gameDataLoaded) return;
    if (authFailed || !user) {
      router.push("/login");
      return;
    }
    if (!user.team) {
      const timer = setTimeout(() => router.push("/onboarding"), 1500);
      return () => clearTimeout(timer);
    }
  }, [gameDataLoaded, authFailed, user, router]);

  const surggies = useGameStore((s) => s.surggies);

  useEffect(() => {
    if (surggies.length > 0) {
      const brain = surggies.find((s) => s.isPrimary) ?? surggies[0];
      if (brain && !activePanel) {
        setActiveSurggieId(brain.id);
        setActivePanel("chat");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surggies.length]);

  if (use3D) {
    return (
      <GameErrorBoundary>
        <GameCanvas3D />
      </GameErrorBoundary>
    );
  }

  return (
    <GameSystemsProvider>
      <GameErrorBoundary>
        <div className="absolute inset-0">
          <GameCanvas className="w-full h-full" />
        </div>
      </GameErrorBoundary>

      <HudBar />
      <MinimapHost />
      <NotificationToast />

      {activePanel === "chat" && <ChatPanel />}
      {activePanel === "section" && <SectionPanel />}
      {activePanel === "tasks" && <TasksPanel />}
    </GameSystemsProvider>
  );
}

function DelegationStreamHost() {
  useDelegationStream();
  return null;
}

export default function GamePage() {
  return (
    <>
      <DelegationStreamHost />
      <Suspense fallback={null}>
        <GamePageInner />
      </Suspense>
    </>
  );
}
